import {
  AI_PAUSED_REASON_LABELS,
  HANDOFF_REASON_LABELS,
  needsHuman,
  relTime,
  resolveHandoff,
  setAiEnabled,
  type Agent,
  type ChatPermission,
  type Conversation,
} from "../../services/chatService";

// ===========================================================================
// Chat — human handoff UI pieces (kept out of ChatInbox.tsx on purpose).
//
//   NeedsHumanBadge  list row badge «Requiere atención humana»
//   AiStatusBanner   in-thread banner: the AI handed the conversation to a person
//                    (reason + summary + assigned agent + actions), or the AI
//                    paused itself because of a guardrail (rate_limit / loop).
// ===========================================================================

function agoLabel(at: number): string {
  const rel = relTime(at);
  return rel === "ahora" ? "ahora" : `hace ${rel}`;
}

export function NeedsHumanBadge({ conv }: { conv: Conversation }) {
  if (!needsHuman(conv)) return null;
  const reason = conv.handoffReason ? HANDOFF_REASON_LABELS[conv.handoffReason] : null;
  return (
    <span
      className="inline-flex flex-shrink-0 items-center gap-0.5 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300"
      title={reason ? `Requiere atención humana — ${reason}` : "Requiere atención humana"}
    >
      <i className="ri-user-voice-line" />
      Requiere atención humana
    </span>
  );
}

export function AiStatusBanner({
  conv,
  agentById,
  can,
}: {
  conv: Conversation;
  agentById: Record<string, Agent>;
  // Backend guards: handoff-resolve needs `reply`, ai-toggle needs `ai_toggle`.
  // Hide what would only come back as a 403 (same treatment as the rest of #524).
  can: Record<ChatPermission, boolean>;
}) {
  if (needsHuman(conv)) {
    const reason = conv.handoffReason ? HANDOFF_REASON_LABELS[conv.handoffReason] : HANDOFF_REASON_LABELS.otro;
    const agent = conv.assignedAgentId ? agentById[conv.assignedAgentId] : null;
    return (
      <div
        role="status"
        className="mx-4 mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-800 dark:text-amber-200 md:mx-12 lg:mx-24"
      >
        <div className="flex flex-wrap items-start gap-2">
          <i className="ri-user-voice-line mt-0.5 text-base" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold">
              Requiere atención humana · {reason}
              {conv.handoffAt ? <span className="ml-1 font-normal opacity-70">({agoLabel(conv.handoffAt)})</span> : null}
            </p>
            {conv.handoffSummary && <p className="mt-0.5 whitespace-pre-wrap break-words">{conv.handoffSummary}</p>}
            <p className="mt-1 opacity-80">
              La IA dejó de responder en esta conversación
              {conv.handoffSource === "rule" ? " (regla automática)" : ""}.{" "}
              {agent ? `Asignada a ${agent.name}.` : conv.assignedAgentId ? "Asignada a un asesor." : "Sin asesor asignado."}
            </p>
          </div>
          <div className="flex flex-shrink-0 gap-1.5">
            {can.reply && (
              <button
                onClick={() => resolveHandoff(conv.id)}
                title="Quitar la marca de atención humana; la IA sigue en pausa"
                className="rounded-md border border-amber-600/50 px-2 py-1 font-medium hover:bg-amber-500/15"
              >
                Marcar como atendida
              </button>
            )}
            {can.ai_toggle && (
              <button
                onClick={() => setAiEnabled(conv.id, true)}
                title="Reactivar la IA en esta conversación (también quita la marca de atención humana)"
                className="rounded-md border border-emerald-600/50 px-2 py-1 font-medium text-emerald-700 hover:bg-emerald-500/15 dark:text-emerald-300"
              >
                Reactivar IA
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Guardrail pause (the AI stopped itself, no handoff pending).
  if (!conv.aiEnabled && conv.aiPausedReason && conv.aiPausedReason !== "handoff") {
    return (
      <div
        role="status"
        className="mx-4 mt-3 flex items-start gap-2 rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs text-sky-800 dark:text-sky-200 md:mx-12 lg:mx-24"
      >
        <i className="ri-robot-2-line mt-0.5 text-base" />
        <span className="min-w-0 flex-1">
          <strong>IA en pausa automática:</strong> {AI_PAUSED_REASON_LABELS[conv.aiPausedReason]}. Revisa la conversación y
          reactiva la IA cuando corresponda.
        </span>
      </div>
    );
  }
  return null;
}
